import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../styles/CreateEvent.module.css";

export default function CreateEvent() {
  const { clubId } = useParams();
  const [club, setClub] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [maxAttendees, setMaxAttendees] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchClub = async () => {
      if (!clubId) return;

      try {
        const clubRef = doc(db, "clubs", clubId);
        const clubSnap = await getDoc(clubRef);

        if (clubSnap.exists()) {
          setClub({ id: clubSnap.id, ...clubSnap.data() });
        }
      } catch (err) {
        console.error("Error fetching club:", err);
      }
    };

    fetchClub();
  }, [clubId]);

  const handleCreateEvent = async () => {
    if (!title.trim() || !date || !time) {
      alert("Please fill in all required fields");
      return;
    }

    if (!user) {
      alert("Please login first");
      return;
    }

    setLoading(true);
    try {
      const eventDateTime = new Date(`${date}T${time}`);

      // Create event
      await addDoc(collection(db, "events"), {
        clubId: clubId,
        clubName: club?.name || "Unknown Club",
        title: title.trim(),
        description: description.trim(),
        date: eventDateTime,
        location: location.trim() || "Online",
        maxAttendees: maxAttendees ? parseInt(maxAttendees) : null,
        createdBy: {
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email.split('@')[0]
        },
        createdAt: serverTimestamp(),
        attendees: [user.uid],
        attendeeCount: 1,
        status: "upcoming"
      });

      // Update club event count
      if (clubId) {
        const clubRef = doc(db, "clubs", clubId);
        await updateDoc(clubRef, {
          events: (club?.events || 0) + 1,
          updatedAt: serverTimestamp()
        });
      }

      alert("Event created successfully!");
      navigate(clubId ? `/club/${clubId}` : "/events");
    } catch (err) {
      console.error("Error creating event:", err);
      alert("Failed to create event: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{club ? `Create Event for ${club.name}` : "Create New Event"}</h2>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Event Title *
        </label>
        <input
          type="text"
          placeholder="e.g., Coding Workshop, Football Match"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Description
        </label>
        <textarea
          placeholder="Describe the event..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="4"
          className={styles.textarea}
        />
      </div>

      <div className={styles.grid}>
        <div>
          <label className={styles.label}>
            Date *
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className={styles.input}
          />
        </div>

        <div>
          <label className={styles.label}>
            Time *
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Location
        </label>
        <input
          type="text"
          placeholder="e.g., Main Auditorium, Online"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          Maximum Attendees (optional)
        </label>
        <input
          type="number"
          placeholder="Leave empty for unlimited"
          value={maxAttendees}
          onChange={(e) => setMaxAttendees(e.target.value)}
          min="1"
          className={styles.input}
        />
      </div>

      <button
        onClick={handleCreateEvent}
        disabled={loading || !title.trim() || !date || !time}
        className={styles.submitBtn}
      >
        {loading ? "Creating Event..." : "Create Event"}
      </button>

      <button
        onClick={() => navigate(-1)}
        className={styles.cancelBtn}
      >
        Cancel
      </button>
    </div>
  );
}